/**
 * WORK-ROUTING-QA-PAGE-BUILD-DOCS-RUNBOOK-01 — pure helpers + wire types
 * for the internal work-routing QA page (`/dashboard/owner/work-routing-qa`).
 *
 * Pure by construction — no React, no fetch, no Supabase, no `Date.now()`
 * — mirroring the `leads-workspace-view.ts` / `patient-master-view.ts`
 * doctrine so every parser, formatter, and access decision unit-tests
 * without jsdom.
 *
 * SCOPE / SAFETY POSTURE (this surface is QA visibility ONLY):
 *   - Owner-scoped: the backend QA endpoints all call requireOwnerScope,
 *     so the page audience is the owner only; the server resolves the
 *     organization from the authenticated scope and IGNORES any
 *     client-supplied organizationId. Nothing here is an access decision
 *     and nothing here ever sends an organization id to the server.
 *   - PHI-safe: routing_config / lead_actions carry no clinical field and
 *     no raw contact value. This module formats ids / roles / status /
 *     template / timestamps only. `createdByPrincipal` is on the wire
 *     (an opaque, encrypted, non-PHI principal) but is DELIBERATELY never
 *     surfaced — token-like values are not displayed.
 *   - The only template this page routes is `new_lead_followup`.
 *   - This page NEVER enables the lead-capture hook and NEVER flips the
 *     visible-QA flag (both are deployed-env / founder decisions).
 */

import type { DashboardMe } from '@/dashboard/types';

// ─── Wire types (PHI-free; mirror lib/work-routing/evaluator.ts) ─────────
// Frontend mirror of the backend evaluator's public shapes, kept in
// lockstep by the established separate-packages convention. The backend
// returns camelCase rows (already mapped from snake_case columns).

/** A routing_config row as served by GET /api/work-routing/qa/routing-config. */
export interface WorkRoutingConfigRow {
  id: number;
  organizationId: number;
  clinicId: number | null;
  workflowTemplateCode: string;
  targetRole: string;
  targetUserId: number | null;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * A lead_actions row as served by GET /api/work-routing/qa/lead-actions
 * (and nested inside the route-lead result). `createdByPrincipal` is part
 * of the wire contract but is never rendered by this surface.
 */
export interface WorkRoutingLeadActionRow {
  id: number;
  organizationId: number;
  clinicId: number | null;
  leadId: number;
  workflowTemplateCode: string;
  assignedRole: string | null;
  assignedUserId: number | null;
  status: string;
  /** Opaque, encrypted, non-PHI principal. NEVER displayed (see header). */
  createdByPrincipal: string;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Result of POST /api/work-routing/qa/route-lead. */
export interface WorkRoutingRouteResult {
  outcome: 'existing' | 'created';
  action: WorkRoutingLeadActionRow;
}

/**
 * The single workflow template this QA page routes. Frontend mirror of the
 * backend `NEW_LEAD_FOLLOWUP` constant (lib/work-routing/templates.ts);
 * kept in lockstep by review.
 */
export const WORK_ROUTING_QA_TEMPLATE_CODE = 'new_lead_followup';

// ─── Flag / access state (fail-closed) ───────────────────────────────────

/**
 * True only when the server-computed `workRoutingQaEnabled` signal is
 * present and exactly `true` — present ONLY when WORK_ROUTING_VISIBLE_QA_ENABLED
 * is on in the deployed backend AND the role is the owner. ABSENT means
 * disabled (the flag is off, or the role is not the owner): the page then
 * renders the disabled state and fires NO QA request. Backend route auth
 * remains the real security boundary.
 */
export function isWorkRoutingQaEnabledForMe(me: DashboardMe | null): boolean {
  return me?.workRoutingQaEnabled === true;
}

/** Boolean-only flag status for the QA page's status panel (no env strings). */
export interface WorkRoutingQaFlagStatus {
  /** WORK_ROUTING_VISIBLE_QA_ENABLED (on + owner ⇒ this page is live). */
  readonly visibleQaEnabled: boolean;
}

/**
 * Derive the boolean flag status from `/me`. When the page is disabled
 * (signal absent) it reads false - honest. (The lead-capture hook flag and
 * its read-only /me status were retired in S1-P5-WORK-QUEUE-FIRST-SLICE; the
 * hook now runs by default.)
 */
export function workRoutingQaFlagStatus(
  me: DashboardMe | null,
): WorkRoutingQaFlagStatus {
  return {
    visibleQaEnabled: me?.workRoutingQaEnabled === true,
  };
}

// ─── Lead id input parsing (mirror backend parseRequiredLeadId) ──────────

export type ParsedLeadId =
  | { ok: true; leadId: number }
  | { ok: false; reason: string };

/**
 * Parse the lead-id text input into a positive integer, mirroring the
 * backend's required-leadId rule (positive integer ≥ 1). Pure: no trust
 * decision, just a client-side guard so a route is never attempted with a
 * blank or malformed id (the server validates again regardless).
 */
export function parseLeadIdInput(raw: string): ParsedLeadId {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: false, reason: 'Enter a lead id first.' };
  }
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 1) {
    return { ok: false, reason: 'Lead id must be a positive whole number.' };
  }
  return { ok: true, leadId: n };
}

/**
 * Build the route-lead request body. By contract this carries ONLY the
 * leadId — never an organizationId / clinicId (the server derives the
 * tenant from the scoped lead row).
 */
export function buildRouteLeadRequest(leadId: number): { leadId: number } {
  return { leadId };
}

// ─── Display formatting (PHI-free; nullable-safe) ────────────────────────

/** Render a nullable number/string for a QA cell; null/undefined ⇒ "—". */
export function fmtCell(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/** A labelled key/value pair for the route result panel. */
export interface RouteResultField {
  readonly label: string;
  readonly value: string;
}

/**
 * The exact, ordered fields the route result panel renders — the set named
 * in the package: outcome, lead_id, organization_id, clinic_id,
 * workflow_template_code, assigned_role, assigned_user_id, status.
 * `created_by_principal` is intentionally excluded.
 */
export function routeResultFields(
  result: WorkRoutingRouteResult,
): RouteResultField[] {
  const a = result.action;
  return [
    { label: 'outcome', value: result.outcome },
    { label: 'lead_id', value: fmtCell(a.leadId) },
    { label: 'organization_id', value: fmtCell(a.organizationId) },
    { label: 'clinic_id', value: fmtCell(a.clinicId) },
    { label: 'workflow_template_code', value: fmtCell(a.workflowTemplateCode) },
    { label: 'assigned_role', value: fmtCell(a.assignedRole) },
    { label: 'assigned_user_id', value: fmtCell(a.assignedUserId) },
    { label: 'status', value: fmtCell(a.status) },
  ];
}

/** Column headers for the routing_config table (display order). */
export const ROUTING_CONFIG_COLUMNS: readonly string[] = [
  'id',
  'clinic_id',
  'workflow_template_code',
  'target_role',
  'target_user_id',
  'is_active',
  'created_at',
];

/** One routing_config row as ordered display cells (PHI-free). */
export function routingConfigRowCells(row: WorkRoutingConfigRow): string[] {
  return [
    fmtCell(row.id),
    fmtCell(row.clinicId),
    fmtCell(row.workflowTemplateCode),
    fmtCell(row.targetRole),
    fmtCell(row.targetUserId),
    row.isActive ? 'true' : 'false',
    fmtCell(row.createdAt),
  ];
}

/** Column headers for the lead_actions table (display order). */
export const LEAD_ACTION_COLUMNS: readonly string[] = [
  'id',
  'lead_id',
  'clinic_id',
  'workflow_template_code',
  'assigned_role',
  'assigned_user_id',
  'status',
  'created_at',
];

/**
 * One lead_actions row as ordered display cells (PHI-free). Deliberately
 * omits `created_by_principal` (opaque token-like ciphertext).
 */
export function leadActionRowCells(row: WorkRoutingLeadActionRow): string[] {
  return [
    fmtCell(row.id),
    fmtCell(row.leadId),
    fmtCell(row.clinicId),
    fmtCell(row.workflowTemplateCode),
    fmtCell(row.assignedRole),
    fmtCell(row.assignedUserId),
    fmtCell(row.status),
    fmtCell(row.createdAt),
  ];
}
